import { useEffect, useState } from 'react'
import {
  FONTS,
  FONT_ACCEPT,
  FONT_KEYS,
  fontStack,
  GOOGLE_FONT_SUGGESTIONS,
  MAX_FONT_BYTES,
  megabytes,
  type FontDto,
  type FontKey,
} from '@praesto/shared'
import { api, ApiError } from '../api.js'
import { useText } from '../i18n.js'
import { refreshFontStylesheet } from '../fonts.js'

/**
 * Ein Schriftwähler: eingebaute Stapel und die geholten Familien derselben
 * Organisation. Beide stehen im selben Feld, weil sie für alles, was danach
 * kommt, dasselbe sind — ein Name, der zu einem Schriftstapel wird.
 */
export function FontSelect({
  label,
  value,
  fonts,
  onChange,
  showPreview = false,
}: {
  label: string
  value: FontKey
  fonts: FontDto[]
  onChange: (font: FontKey) => void
  showPreview?: boolean
}): React.ReactElement {
  // Solange die Schriftenliste noch lädt, gäbe es zu einer gewählten eigenen
  // Familie keinen Eintrag — der Wähler zeigte dann die Grundschrift an,
  // während in Wahrheit etwas anderes eingestellt ist. Deshalb bekommt der
  // aktuelle Wert notfalls einen eigenen Eintrag.
  const known =
    (FONTS as Record<string, unknown>)[value] !== undefined ||
    fonts.some((font) => font.family === value)

  const t = useText()

  return (
    <label className="field">
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        {!known && <option value={value}>{value}</option>}
        <optgroup label={t('fonts.builtIn')}>
          {FONT_KEYS.map((font) => (
            <option key={font} value={font}>
              {t(FONTS[font].label)}
            </option>
          ))}
        </optgroup>
        {fonts.length > 0 && (
          <optgroup label={t('fonts.added')}>
            {fonts.map((font) => (
              <option key={font.id} value={font.family}>
                {font.family}
              </option>
            ))}
          </optgroup>
        )}
      </select>
      {showPreview && (
        <span className="font-preview" style={{ fontFamily: fontStack(value) }}>
          Præsto — Kundenpräsentation Q3
        </span>
      )}
    </label>
  )
}

/** Ein Schnitt, wie ihn der Server erwartet. */
interface FontCut {
  family: string
  weight: number
  style: 'normal' | 'italic'
}

/**
 * Was der Dateiname über den Schnitt verrät.
 *
 * Schriftdateien heißen fast immer `Familie-SemiBoldItalic.otf`. Das als
 * Vorschlag zu lesen erspart drei Eingaben je Datei; falsch geraten ist nicht
 * schlimm, denn alle drei Felder bleiben änderbar. Die Reihenfolge ist wichtig:
 * „SemiBold" enthält „Bold".
 */
const WEIGHT_NAMES: readonly (readonly [RegExp, number])[] = [
  [/thin|hairline/i, 100],
  [/extra[- ]?light|ultra[- ]?light/i, 200],
  [/semi[- ]?bold|demi[- ]?bold/i, 600],
  [/extra[- ]?bold|ultra[- ]?bold/i, 800],
  [/black|heavy/i, 900],
  [/light/i, 300],
  [/medium/i, 500],
  [/bold/i, 700],
  [/regular|normal|book/i, 400],
]

export function guessCut(fileName: string): FontCut {
  const base = fileName.replace(/\.[^.]+$/, '').replace(/[_]+/g, '-')
  const style = /italic|oblique|kursiv/i.test(base) ? 'italic' : 'normal'
  const weight = WEIGHT_NAMES.find(([pattern]) => pattern.test(base))?.[1] ?? 400
  const family = base
    .replace(
      /[-\s]*(thin|hairline|extra[- ]?light|ultra[- ]?light|semi[- ]?bold|demi[- ]?bold|extra[- ]?bold|ultra[- ]?bold|black|heavy|light|medium|bold|regular|normal|book|italic|oblique|kursiv)/gi,
      '',
    )
    .replace(/[-]+/g, ' ')
    // Alles, was der Server im Namen nicht zulässt, gleich hier heraus.
    .replace(/[^\p{L}\p{N} .&+-]/gu, '')
    .trim()
  return { family: family || base.replace(/[^\p{L}\p{N} .&+-]/gu, '').trim(), weight, style }
}

/**
 * Der Schriftenbestand einer Organisation.
 *
 * Steht an zwei Stellen: im Standard-Design der Organisation, wo er hingehört,
 * und im Design-Fenster eines Boards, wo er gebraucht wird — wer beim Gestalten
 * merkt, dass die Hausschrift fehlt, soll sie nicht erst über die Übersicht
 * nachtragen müssen.
 */
export function FontLibrary({
  orgId,
  fonts,
  onChange,
  canRemove,
}: {
  orgId: string
  fonts: FontDto[]
  onChange: (fonts: FontDto[]) => void
  /** Entfernen darf nur die Verwaltung — sonst zieht jemand die Schrift unter laufenden Boards weg. */
  canRemove: boolean
}): React.ReactElement {
  const t = useText()
  const [wanted, setWanted] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** Die gewählte Datei samt vorgeschlagenem Schnitt, solange sie nicht abgeschickt ist. */
  const [pending, setPending] = useState<{ file: File; cut: FontCut } | null>(null)
  const [licensed, setLicensed] = useState(false)

  async function add(): Promise<void> {
    const family = wanted.trim()
    if (!family) return
    setBusy(true)
    setError(null)
    try {
      const added = await api.addFont(orgId, family)
      if (!fonts.some((font) => font.id === added.id)) {
        onChange([...fonts, added].sort((a, b) => a.family.localeCompare(b.family, 'de')))
      }
      refreshFontStylesheet()
      setWanted('')
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('fonts.fetchFailed'))
    } finally {
      setBusy(false)
    }
  }

  async function upload(): Promise<void> {
    if (!pending || !licensed) return
    setBusy(true)
    setError(null)
    try {
      const added = await api.uploadFont(orgId, pending.file, pending.cut)
      const rest = fonts.filter((font) => font.id !== added.id)
      onChange([...rest, added].sort((a, b) => a.family.localeCompare(b.family, 'de')))
      refreshFontStylesheet()
      setPending(null)
      setLicensed(false)
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('fonts.uploadFailed'))
    } finally {
      setBusy(false)
    }
  }

  async function remove(font: FontDto): Promise<void> {
    setError(null)
    try {
      await api.removeFont(font.id)
      onChange(fonts.filter((f) => f.id !== font.id))
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : t('fonts.removeFailed'))
    }
  }

  return (
    <>
      <div className="field">
        <span>{t('fonts.addGoogle')}</span>
        <span className="theme-row">
          <input
            list="google-fonts"
            value={wanted}
            onChange={(event) => {
              setWanted(event.target.value)
              setError(null)
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                void add()
              }
            }}
            placeholder={t('fonts.googlePlaceholder')}
          />
          <button className="button ghost" onClick={() => void add()} disabled={busy || !wanted.trim()}>
            {busy ? t('fonts.loading') : t('fonts.fetch')}
          </button>
        </span>
        <datalist id="google-fonts">
          {GOOGLE_FONT_SUGGESTIONS.map((family) => (
            <option key={family} value={family} />
          ))}
        </datalist>
        <small>{t('fonts.googleHint')}</small>
      </div>

      <div className="field">
        <span>{t('fonts.uploadOwn')}</span>
        <input
          type="file"
          accept={FONT_ACCEPT}
          onChange={(event) => {
            const file = event.target.files?.[0]
            event.target.value = ''
            setError(null)
            setLicensed(false)
            if (file) setPending({ file, cut: guessCut(file.name) })
          }}
        />
        <small>{t('fonts.uploadHint', { limit: megabytes(MAX_FONT_BYTES) })}</small>
      </div>

      {pending && (
        <div className="field">
          <span>„{pending.file.name}"</span>
          <span className="theme-row">
            <input
              value={pending.cut.family}
              onChange={(event) =>
                setPending({ ...pending, cut: { ...pending.cut, family: event.target.value } })
              }
              placeholder={t('fonts.namePlaceholder')}
            />
            <select
              value={pending.cut.weight}
              onChange={(event) =>
                setPending({
                  ...pending,
                  cut: { ...pending.cut, weight: Number(event.target.value) },
                })
              }
            >
              <option value={100}>Thin (100)</option>
              <option value={200}>ExtraLight (200)</option>
              <option value={300}>Light (300)</option>
              <option value={400}>{t('fonts.regular')} (400)</option>
              <option value={500}>Medium (500)</option>
              <option value={600}>SemiBold (600)</option>
              <option value={700}>{t('fonts.bold')} (700)</option>
              <option value={800}>ExtraBold (800)</option>
              <option value={900}>Black (900)</option>
            </select>
            <select
              value={pending.cut.style}
              onChange={(event) =>
                setPending({
                  ...pending,
                  cut: { ...pending.cut, style: event.target.value === 'italic' ? 'italic' : 'normal' },
                })
              }
            >
              <option value="normal">{t('fonts.upright')}</option>
              <option value="italic">{t('fonts.italic')}</option>
            </select>
          </span>
          <label className="check">
            <input
              type="checkbox"
              checked={licensed}
              onChange={(event) => setLicensed(event.target.checked)}
            />
            <span>{t('fonts.licenseConfirm')}</span>
          </label>
          <span className="theme-row">
            <button
              className="button ghost"
              onClick={() => void upload()}
              disabled={busy || !licensed || pending.cut.family.trim() === ''}
            >
              {busy ? t('fonts.loading') : t('fonts.upload')}
            </button>
            <button className="button ghost" onClick={() => setPending(null)} disabled={busy}>
              {t('common.cancel')}
            </button>
          </span>
        </div>
      )}

      {error && <p className="form-error">{error}</p>}

      {fonts.length > 0 && (
        <ul className="font-list">
          {fonts.map((font) => (
            <li key={font.id}>
              <span style={{ fontFamily: fontStack(font.family) }}>{font.family}</span>
              {font.orgId === null && (
                <span className="badge subtle">{t('fonts.fromProvider')}</span>
              )}
              {font.source === 'upload' && (
                <span className="badge subtle">{t('fonts.ownFile')}</span>
              )}
              {canRemove && font.orgId !== null && (
                <button
                  className="button ghost icon danger"
                  title={t('common.remove')}
                  onClick={() => void remove(font)}
                >
                  ×
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </>
  )
}

/** Lädt die Schriften einer Organisation; leer, wenn etwas schiefgeht. */
export function useOrgFonts(orgId: string | null): [FontDto[], (fonts: FontDto[]) => void] {
  const [fonts, setFonts] = useState<FontDto[]>([])
  useEffect(() => {
    if (!orgId) return
    let cancelled = false
    api
      .fonts(orgId)
      .then((list) => {
        if (!cancelled) setFonts(list)
      })
      .catch(() => {
        if (!cancelled) setFonts([])
      })
    return () => {
      cancelled = true
    }
  }, [orgId])
  return [fonts, setFonts]
}
